import { Helmet } from 'react-helmet';
import Button from '../../components/ui/Button';
import Surface from '../../components/ui/Surface';

const APPROACH_POINTS = [
  'Integrated communication between your financial advisor and tax preparer',
  'Secure, paperless document collection and e-signature',
  'Year-round tax projections and estimated payment tracking',
  'Capital gains management before year-end',
  'Coordination of Roth conversions, charitable giving, and business income strategies'
];

const PROCESS_STEPS = [
  {
    title: '1. Document Gathering',
    description:
      'We use secure digital portals for W-2s, 1099s, K-1s, and other tax documents — no paper required.'
  },
  {
    title: '2. Analysis & Preparation',
    description:
      'Our preparers review your prior-year return, align it with your current plan, and identify deductions and credits applicable to your situation.'
  },
  {
    title: '3. Review & Filing',
    description:
      'You’ll receive a digital draft of your return to review with your advisor. Once approved, we e-file directly with the IRS and state agencies.'
  },
  {
    title: '4. Post-Filing Planning',
    description:
      'After filing, we summarize takeaways and create a tax strategy outline for the upcoming year.'
  }
];

const AUDIENCE_POINTS = [
  {
    title: 'Individuals & Families',
    description: 'Optimize deductions, investment income, and child credits.'
  },
  {
    title: 'Business Owners',
    description: 'Maximize Section 199A (Qualified Business Income), depreciation, and expense elections.'
  },
  {
    title: 'Retirees',
    description: 'Coordinate RMDs, Social Security, and tax-efficient withdrawals.'
  },
  {
    title: 'Investors',
    description: 'Manage cost basis, wash sales, and capital gains harvesting.'
  }
];

const TECH_STEPS = [
  { title: 'Upload', description: 'Secure Onvio® portal for documents.' },
  { title: 'Review', description: 'Collaborative advisor + preparer workflow.' },
  { title: 'E-Sign', description: 'Paperless approvals with MFA security.' },
  { title: 'File', description: 'UltraTax® e-filing direct to IRS and states.' },
  { title: 'Strategy', description: 'Action items for the year ahead.' }
];

const INTEGRATION_EXAMPLES = [
  'Roth conversions coordinated with portfolio drawdowns',
  'Charitable giving optimized through donor-advised funds',
  'Asset location: placing income-producing investments in tax-advantaged accounts',
  'Ongoing monitoring of legislative updates and tax law changes'
];

export default function TaxPreparationPage() {
  return (
    <main className="section-container py-10 md:py-16">
      <Helmet>
        <title>Tax Preparation | Carter Asset Management</title>
        <meta
          name="description"
          content="Discover Carter Asset Management's integrated tax preparation process that keeps your financial plan and tax return aligned all year."
        />
        <link rel="canonical" href="https://carteram.com/how-we-work/tax-preparation" />
      </Helmet>

      <header className="mb-10 md:mb-12">
        <p className="text-xs font-semibold uppercase tracking-wide text-sky-700/70">How We Work</p>
        <h1 className="mt-2 text-2xl md:text-4xl font-semibold tracking-tight text-slate-900">
          Integrated Tax Preparation for a Complete Financial Picture
        </h1>
        <p className="mt-3 max-w-3xl text-base md:text-lg leading-relaxed text-slate-700">
          From investment income to business deductions, our in-house tax preparation process keeps your strategy aligned across every decision — so your financial plan and tax return tell the same story.
        </p>
        <div className="mt-6">
          <Button href="/contact" variant="primary" className="w-full sm:w-auto">
            Schedule a Consultation
          </Button>
        </div>
      </header>

      <section className="mb-10 md:mb-12">
        <h2 className="text-xl font-semibold text-slate-900 md:text-2xl">More Than Filing — A Year-Round Strategy</h2>
        <p className="mt-3 max-w-3xl text-slate-700">
          We approach tax preparation as an extension of your financial plan — not a once-a-year task. Our process focuses on proactive collaboration between your advisor and tax team, ensuring every investment, withdrawal, or business decision considers its tax impact.
        </p>
        <ul className="mt-4 space-y-2 list-disc pl-5 text-slate-700">
          {APPROACH_POINTS.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>

      <section className="mb-10 md:mb-12">
        <h2 className="text-xl font-semibold text-slate-900 md:text-2xl">A Streamlined Experience from Start to Finish</h2>
        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
          {PROCESS_STEPS.map((step) => (
            <Surface key={step.title} className="h-full p-6 shadow-sm">
              <h3 className="text-base font-semibold text-cam-blue md:text-lg">{step.title}</h3>
              <p className="mt-2 text-base md:text-sm text-slate-700">{step.description}</p>
            </Surface>
          ))}
        </div>
      </section>

      <section className="mb-10 md:mb-12">
        <h2 className="text-xl font-semibold text-slate-900 md:text-2xl">Tailored Tax Services for Every Stage of Wealth</h2>
        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
          {AUDIENCE_POINTS.map((item) => (
            <Surface key={item.title} className="h-full p-6 shadow-sm">
              <h3 className="text-base font-semibold text-slate-900">{item.title}</h3>
              <p className="mt-2 text-base md:text-sm text-slate-700">{item.description}</p>
            </Surface>
          ))}
        </div>
      </section>

      <section className="mb-10 md:mb-12">
        <h2 className="text-xl font-semibold text-slate-900 md:text-2xl">Simple, Secure, and Smart</h2>
        <p className="mt-3 max-w-3xl text-slate-700">
          Our process is 100% digital — from upload to signature. We use Onvio® by Thomson Reuters for secure client document exchange and UltraTax® for accurate e-filing. Encryption, multi-factor authentication, and compliance monitoring protect every step of the process.
        </p>
        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-5">
          {TECH_STEPS.map((item) => (
            <Surface key={item.title} className="h-full p-5 text-center shadow-sm">
              <p className="text-base font-semibold text-cam-blue md:text-sm">{item.title}</p>
              <p className="mt-2 text-sm text-slate-600 md:text-xs">{item.description}</p>
            </Surface>
          ))}
        </div>
      </section>

      <section className="mb-10 md:mb-12">
        <h2 className="text-xl font-semibold text-slate-900 md:text-2xl">Tax Planning and Wealth Management — Working Together</h2>
        <p className="mt-3 max-w-3xl text-slate-700">
          Your investment strategy, retirement plan, and tax return shouldn’t operate in silos. By integrating both under one advisory relationship, we can make real-time decisions that minimize taxes and enhance long-term outcomes.
        </p>
        <ul className="mt-4 space-y-2 list-disc pl-5 text-slate-700">
          {INTEGRATION_EXAMPLES.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>

      <section className="mt-12 rounded-2xl bg-slate-50 p-6 text-center ring-1 ring-slate-200/80 md:p-8">
        <h2 className="text-xl font-semibold text-slate-900 md:text-2xl">Ready to Simplify Your Tax Season?</h2>
        <p className="mt-3 text-base leading-relaxed text-slate-700">
          Let’s connect your wealth management and tax strategy. Schedule a no-cost consultation to see how we can simplify your filings and strengthen your financial outcomes.
        </p>
        <div className="mt-6 flex justify-center">
          <Button href="/contact" variant="primary" className="w-full sm:w-auto">
            Start a Consultation
          </Button>
        </div>
      </section>
    </main>
  );
}
